// React & Libraries
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save } from "react-feather";
import Swal from "sweetalert2";

// Reactstrap
import {
  Col,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  ModalBody,
  Row,
  Spinner,
} from "reactstrap";

// Components
import { Btn, H5, P } from "../../../../../../AbstractElements";

// Services
import {
  employeeStatusService,
  VIEW_EMPSTATUS_QUERY_KEY,
} from "../../../../../../Services/employeeStatusServices";

interface UpdateEmpStatusModalProps {
  toggle?: () => void;
  id: number;
}

const UpdateEmpStatusForm = ({ toggle, id }: UpdateEmpStatusModalProps) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: "",
    status: -1,
  });

  const { data: dataResponse, isLoading: isFetching } = useQuery({
    queryKey: [VIEW_EMPSTATUS_QUERY_KEY, id],
    queryFn: () => employeeStatusService.showEmployeeStatus(id),
    enabled: !!id,
  });

  useEffect(() => {
    if (dataResponse) {
      setFormData({
        name: dataResponse.data.name || "",
        status: Number(dataResponse.data.status),
      });
    }
  }, [dataResponse]);

  const updateEmpStatusMutation = useMutation({
    mutationFn: (data: typeof formData) =>
      employeeStatusService.updateEmployeeStatus(id, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: VIEW_EMPSTATUS_QUERY_KEY,
      });
      Swal.close();
      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "success",
        title: "Transaction Successful",
        text:
          result?.message ||
          "Employee Status record has been updated successfully.",
        confirmButtonColor: "#4e73df",
      });
      if (toggle) {
        toggle();
      }
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.message ||
        "Failed to update the Employee Status record. Please try again.";
      Swal.close();
      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "error",
        title: "Transaction Failed",
        text: message,
        confirmButtonColor: "#4e73df",
      });
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    Swal.fire({
      title: "Update Employee Status Record?",
      text: "Are you sure you want to update this Employee Status record?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: '<i class="fa fa-save"></i> Update Record',
      cancelButtonText: '<i class="fa fa-times"></i> Cancel',
      confirmButtonColor: "#4e73df",
      cancelButtonColor: "#dc3545",
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }
      Swal.fire({
        title: "Updating Employee Status Record...",
        text: "Please wait while the record is being updated.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      updateEmpStatusMutation.mutate(formData);
    });
  };

  if (isFetching) {
    return (
      <ModalBody
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "200px" }}
      >
        <Spinner color="primary" />
      </ModalBody>
    );
  }

  return (
    <ModalBody>
      <Form className="needs-validation" onSubmit={handleSubmit}>
        <div className="d-flex justify-content-between align-items-center">
          <H5> </H5>
          <P className="mb-2">
            <i className="icofont icofont-exclamation-tringle txt-warning"></i>{" "}
            Fields marked with <b className="text-danger">*</b> are required{" "}
            <i className="icofont icofont-exclamation-tringle txt-warning"></i>
          </P>
        </div>
        <Row className="g-3">
          <div className="p-3 card-wrapper border rounded-3 h-100">
            <Col md="12">
              <FormGroup>
                <Label>
                  Employee Status <b className="text-danger">*</b>
                </Label>
                <InputGroup>
                  <InputGroupText>
                    <i className="icofont icofont-info-circle txt-primary"></i>
                  </InputGroupText>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    minLength={2}
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                </InputGroup>
              </FormGroup>
            </Col>
          </div>
          <div className="d-flex justify-content-end">
            <Col md="6">
              <Btn
                color="primary"
                className="w-100"
                type="submit"
                disabled={updateEmpStatusMutation.isPending}
              >
                <Save size={16} className="me-1" />
                {updateEmpStatusMutation.isPending ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Updating...
                  </>
                ) : (
                  "Update Record"
                )}
              </Btn>
            </Col>
          </div>
        </Row>
      </Form>
    </ModalBody>
  );
};

export default UpdateEmpStatusForm;
